import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import type { Segment } from '@feathr/blackbox';
import type { IToastAction } from '@feathr/components';
import { useExportModal } from '@feathr/extender/hooks/useExportModal';
import { useFlags, useLocalUrl, useStore } from '@feathr/extender/state';

import type { IUseEmailActivityTabReturn } from './EmailActivityTab/EmailActivityTab.hooks';
import { useEmailActivityTab } from './EmailActivityTab/EmailActivityTab.hooks';
import type { IExportDetailsStepState } from './ExportDetailsStep/ExportDetailsStep';
import type {
  IDeliveryUserFieldSlice,
  IGroupExportTypeHook,
  ISharedFormSlice,
  TGroupExportStep,
  TGroupExportType,
} from './GroupExportModal.types';
import type {
  IEmailActivityExportPayload,
  IGroupExportTypeOption,
  IOtherActivitiesExportPayload,
} from './GroupExportModal.utils';
import {
  collectValidationErrors,
  getGroupExportTypeOptions,
  resolveExportNameTemplate,
} from './GroupExportModal.utils';
import type { IUseOtherActivitiesTabReturn } from './OtherActivitiesTab/OtherActivitiesTab.hooks';
import { useOtherActivitiesTab } from './OtherActivitiesTab/OtherActivitiesTab.hooks';
import type { IUsePersonTabReturn } from './PersonTab/PersonTab.hooks';
import { usePersonTab } from './PersonTab/PersonTab.hooks';
import type { IUseSimpleExportFormReturn } from './SimpleExportForm/SimpleExportForm.hooks';
import { useSimpleExportForm } from './SimpleExportForm/SimpleExportForm.hooks';

export type { TGroupExportStep, TGroupExportType } from './GroupExportModal.types';

const EMAIL_ACTIVITY_PRESET_IDS = ['30d', '60d', '90d'];
const EMAIL_ACTIVITY_DEFAULT_DATE_RANGE = '30d';

export interface IUseGroupExportModalOptions {
  segment: Segment;
  onClose: () => void;
  /** Overrides the live export call; the modal wires the real one when omitted. */
  onExportEmailActivity?: (payload: IEmailActivityExportPayload) => Promise<void>;
  /** Overrides the live export call; the modal wires the real one when omitted. */
  onExportOtherActivities?: (payload: IOtherActivitiesExportPayload) => Promise<void>;
  successToastAction?: IToastAction;
}

export interface IUseGroupExportModalReturn {
  step: TGroupExportStep;
  exportType: TGroupExportType;
  detailsStep: IExportDetailsStepState;
  /** Why the details step can't be left yet; empty when it can. */
  detailsStepErrors: string[];
  isLoading: boolean;
  isSubmitDisabled: boolean;
  personTab: IUsePersonTabReturn;
  emailActivityTab: IUseEmailActivityTabReturn;
  otherActivitiesTab: IUseOtherActivitiesTabReturn;
  simpleExportForm: IUseSimpleExportFormReturn;
  handleNext: () => void;
  handleBack: () => void;
  handleSubmit: () => Promise<void>;
  handleClose: () => void;
}

export function useGroupExportModal({
  segment,
  onClose,
  onExportEmailActivity,
  onExportOtherActivities,
  successToastAction,
}: IUseGroupExportModalOptions): IUseGroupExportModalReturn {
  const { t } = useTranslation();
  const { showEmailActivityExport } = useFlags();
  const history = useHistory();
  const localUrl = useLocalUrl();
  const { SegmentExports } = useStore();

  // Reactive read so MobX re-renders once the segment's name loads.
  const resolvedExportName = resolveExportNameTemplate(segment.get('name', ''));

  /*
   * Every export mode lands on the same dashboard, so all three tabs share this
   * action. The export POST goes out through `wretch` rather than through the
   * SegmentExports collection, so the collection's cached listings never learn
   * about the new row — refetch them before navigating, or the dashboard renders
   * its cached page and the export only appears after a manual browser refresh.
   */
  const viewExportToastAction = useMemo<IToastAction>(
    () => ({
      label: t('View export'),
      onClick: (): void => {
        SegmentExports.refreshApiCache();
        history.push(localUrl('/data/exports'));
      },
    }),
    [SegmentExports, history, localUrl, t],
  );

  const exportEmailActivity = useCallback(
    async (payload: IEmailActivityExportPayload): Promise<void> => {
      await segment.exportEmailActivity(payload);
    },
    [segment],
  );

  const exportOtherActivities = useCallback(
    async (payload: IOtherActivitiesExportPayload): Promise<void> => {
      await segment.exportOtherActivities(payload);
    },
    [segment],
  );

  const sharedExport = useExportModal(t, {
    initialExportName: resolvedExportName,
    presetIds: EMAIL_ACTIVITY_PRESET_IDS,
    defaultDateRange: EMAIL_ACTIVITY_DEFAULT_DATE_RANGE,
  });

  const [step, setStep] = useState<TGroupExportStep>('details');
  const [exportType, setExportType] = useState<TGroupExportType>('person');

  const exportTypeOptions = useMemo(() => getGroupExportTypeOptions(t), [t]);

  const handleChangeExportType = useCallback((option: IGroupExportTypeOption): void => {
    setExportType(option.id);
  }, []);

  const sharedFormSlice: ISharedFormSlice = {
    exportName: sharedExport.exportName,
    exportNameError: sharedExport.validationErrors?.exportName,
    email: sharedExport.email,
    emailError: sharedExport.validationErrors?.email,
  };

  const deliveryUserFieldSlice: IDeliveryUserFieldSlice = {
    deliveryUser: sharedExport.deliveryUser,
    emailHadFocus: sharedExport.emailHadFocus,
    handleChangeDeliveryUser: sharedExport.handleChangeDeliveryUser,
    handleClearDeliveryUser: sharedExport.handleClearDeliveryUser,
  };

  const detailsStep: IExportDetailsStepState = {
    ...sharedFormSlice,
    ...deliveryUserFieldSlice,
    exportTypeOptions,
    selectedExportTypeOption: exportTypeOptions.find((option) => option.id === exportType),
    handleChangeExportType,
    exportNameHadFocus: sharedExport.exportNameHadFocus,
    handleChangeExportName: sharedExport.handleChangeExportName,
    handleExportNameBlur: sharedExport.handleExportNameBlur,
  };

  /*
   * Only the two fields this step owns. The date range is validated on step 2, where
   * it is shown — folding it in here would block Next with an error the user cannot see.
   */
  const detailsStepErrors = collectValidationErrors(
    sharedFormSlice.exportNameError,
    sharedFormSlice.emailError,
  );

  const personTab = usePersonTab({
    segment,
    form: sharedFormSlice,
    handleExport: sharedExport.handleExport,
    successToastAction: successToastAction ?? viewExportToastAction,
  });

  const dateRangeSection = {
    selectedDateRange: sharedExport.selectedDateRange,
    customDateRange: sharedExport.customDateRange,
    dateRangeHadFocus: sharedExport.dateRangeHadFocus,
    dateRangeOptions: sharedExport.dateRangeOptions,
    dateRangeError: sharedExport.validationErrors?.dateRange,
    handleDateRangeChange: sharedExport.handleDateRangeChange,
    handleCustomDateRangeChange: sharedExport.handleCustomDateRangeChange,
  };

  const emailActivityTab = useEmailActivityTab({
    form: sharedFormSlice,
    dateRange: dateRangeSection,
    resolveDateRange: sharedExport.resolveDateRange,
    handleExport: sharedExport.handleExport,
    onExportEmailActivity: onExportEmailActivity ?? exportEmailActivity,
    successToastAction: successToastAction ?? viewExportToastAction,
  });

  const otherActivitiesTab = useOtherActivitiesTab({
    form: sharedFormSlice,
    dateRange: dateRangeSection,
    resolveDateRange: sharedExport.resolveDateRange,
    handleExport: sharedExport.handleExport,
    onExportOtherActivities: onExportOtherActivities ?? exportOtherActivities,
    successToastAction: successToastAction ?? viewExportToastAction,
  });

  /*
   * Flag-off form; carries the People/Activity export-type toggle the legacy
   * modal had. It gets no toast action: `showEmailActivityExport` also gates the
   * /data/exports route, so a link from this form would resolve to a 404.
   */
  const simpleExportForm = useSimpleExportForm({
    segment,
    form: sharedFormSlice,
    deliveryUserField: deliveryUserFieldSlice,
    handleExport: sharedExport.handleExport,
  });

  const handleNext = useCallback((): void => {
    if (detailsStepErrors.length > 0) {
      return;
    }
    setStep('configure');
  }, [detailsStepErrors.length]);

  const handleBack = useCallback((): void => {
    setStep('details');
  }, []);

  const handleClose = useCallback((): void => {
    sharedExport.resetFormState();
    personTab.resetState();
    emailActivityTab.resetState();
    otherActivitiesTab.resetState();
    simpleExportForm.resetState();
    setStep('details');
    setExportType('person');
    onClose();
  }, [
    emailActivityTab.resetState,
    onClose,
    otherActivitiesTab.resetState,
    personTab.resetState,
    sharedExport.resetFormState,
    simpleExportForm.resetState,
  ]);

  // Record lookup so adding a new TGroupExportType id raises a TS error here.
  const hooksByExportType: Record<TGroupExportType, IGroupExportTypeHook<unknown> | undefined> = {
    person: personTab,
    emailActivity: emailActivityTab,
    otherActivities: otherActivitiesTab,
  };
  // With the flag off there is no wizard; the single-pane form is the active surface.
  const activeExportState = showEmailActivityExport
    ? hooksByExportType[exportType]
    : simpleExportForm;

  const isSubmitDisabled =
    sharedExport.isLoading ||
    activeExportState === undefined ||
    activeExportState.validationErrors.length > 0;

  const handleSubmit = useCallback(async (): Promise<void> => {
    const didSucceed = (await activeExportState?.handleSubmit()) ?? false;
    if (didSucceed) {
      handleClose();
    }
  }, [activeExportState, handleClose]);

  return {
    step,
    exportType,
    detailsStep,
    detailsStepErrors,
    isLoading: sharedExport.isLoading,
    isSubmitDisabled,
    personTab,
    emailActivityTab,
    otherActivitiesTab,
    simpleExportForm,
    handleNext,
    handleBack,
    handleSubmit,
    handleClose,
  };
}
